import { functions } from "../firebase";

// Task dispatch for the Brain screen. Every call goes through a callable, so
// the Firebase id token travels automatically and requireBrainOperator runs
// server-side — nothing here is a security boundary.
//
// Catalog tasks carry no prompt: the caller picks a task the server publishes
// and fills in its declared fields, and the prompt is composed on
// tribed-workers from a server-side template.
//
// `freeform` is the exception — its prompt reaches `claude -p` verbatim. The
// server publishes it only to role 101 and re-checks it on dispatch, so the
// panel simply renders whatever brainCatalog returns. Never infer the caller's
// rights from what is on screen: the absence of a composer is a consequence of
// the gate, not the gate itself.

/** The one task whose single `prompt` field is free text rather than a form. */
export const FREEFORM_TASK_ID = "freeform";

export type BrainTaskField = {
  name: string;
  required: boolean;
};

export type BrainTask = {
  id: string;
  label: string;
  fields: BrainTaskField[];
};

export type BrainJobStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "rejected";

export type BrainJob = {
  id: string;
  taskId: string;
  params: Record<string, string>;
  status: BrainJobStatus;
  error: string;
  deliverable: string;
  logTail?: string;
  requestedBy: { uid: string; email: string } | null;
  requestedAt: number | null;
  finishedAt: number | null;
};

/** Callable errors arrive as {code, message}; surface the message verbatim. */
const call = async <T,>(name: string, payload?: unknown): Promise<T> => {
  try {
    const result = await functions.httpsCallable(name)(payload || {});
    return result.data as T;
  } catch (e: any) {
    throw new Error(e?.message || `${name} failed.`);
  }
};

export const fetchBrainCatalog = (): Promise<{ tasks: BrainTask[] }> =>
  call("brainCatalog");

export const dispatchBrainJob = (
  taskId: string,
  params: Record<string, string>
): Promise<{ jobId: string; status: BrainJobStatus }> =>
  call("brainDispatch", { taskId, params });

export const fetchBrainJobs = (limit = 25): Promise<{ jobs: BrainJob[] }> =>
  call("brainJobs", { limit });

export const fetchBrainJob = (jobId: string): Promise<{ job: BrainJob }> =>
  call("brainJobs", { jobId });
